// Portfolio Administration v1.0 PR 7 — shared metrics-card primitives.
// One file with several small components so the row sections stay terse.

import type { ReactNode } from "react";
import { Card } from "@/design-system/components/ui";

export interface MetricCardProps {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  testId?: string;
}

export function MetricCard({ label, value, caption, testId }: MetricCardProps) {
  return (
    <Card className="flex flex-col gap-space-1 p-space-3" data-testid={testId}>
      <span className="text-caption text-gray-mid uppercase tracking-wide">
        {label}
      </span>
      <span className="font-display font-bold text-h2 text-onyx leading-none">
        {value}
      </span>
      {caption && (
        <span className="text-small text-gray-mid leading-tight">{caption}</span>
      )}
    </Card>
  );
}

export interface MetricCardWithChartProps extends MetricCardProps {
  children: ReactNode;
}

export function MetricCardWithChart({
  label,
  value,
  caption,
  children,
  testId,
}: MetricCardWithChartProps) {
  return (
    <Card className="flex flex-col gap-space-2 p-space-3" data-testid={testId}>
      <div className="flex flex-col gap-space-1">
        <span className="text-caption text-gray-mid uppercase tracking-wide">
          {label}
        </span>
        <span className="font-display font-bold text-h3 text-onyx leading-none">
          {value}
        </span>
        {caption && (
          <span className="text-small text-gray-mid leading-tight">
            {caption}
          </span>
        )}
      </div>
      <div className="flex-1 min-h-[120px]">{children}</div>
    </Card>
  );
}

export function MetricsCardRow({
  title,
  children,
  testId,
}: {
  title: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section
      className="flex flex-col gap-space-2"
      data-testid={testId}
      aria-label={title}
    >
      <h2 className="font-display font-bold text-h3 text-onyx m-0">{title}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-space-3">
        {children}
      </div>
    </section>
  );
}

export function formatMoney(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPercent(value: number | null | undefined, digits = 2): string {
  if (value == null) return "—";
  return `${(value * 100).toFixed(digits)}%`;
}

export function formatNumber(value: number | null | undefined): string {
  if (value == null) return "—";
  return new Intl.NumberFormat("en-US").format(value);
}

export function ConcentrationTable({
  rows,
  emptyLabel = "No data",
}: {
  rows: { label: string; exposure: number; loanCount: number }[];
  emptyLabel?: string;
}) {
  if (!rows.length) {
    return (
      <p className="text-small text-gray-mid" data-testid="concentration-empty">
        {emptyLabel}
      </p>
    );
  }
  return (
    <table className="w-full text-small">
      <thead>
        <tr className="text-caption text-gray-mid">
          <th className="text-left font-normal pb-space-1">Name</th>
          <th className="text-right font-normal pb-space-1">Exposure</th>
          <th className="text-right font-normal pb-space-1">Loans</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.label} className="border-t border-border-light">
            <td className="py-space-1">{r.label}</td>
            <td className="py-space-1 text-right">{formatMoney(r.exposure)}</td>
            <td className="py-space-1 text-right">{r.loanCount}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
